import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Play, Pause, SkipBack, SkipForward, Clock, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PassageDateDisplay } from './PassageDateDisplay';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';

// Memoized component for era label content
const EraLabelContent = memo(({ era, isHovered, formatYear }: {
    era: TimelineEra;
    isHovered: boolean;
    formatYear: (year: number) => string;
}) => (
    <div className="flex flex-col items-start">
        <span className={`text-[11px] md:text-xs font-medium ${isHovered ? 'text-foreground' : 'text-muted-foreground'}`}>
            {era.label}
        </span>
        <span className="text-[9px] md:text-[10px] text-muted-foreground/70">
            {formatYear(era.startYear)} - {formatYear(era.endYear)}
        </span>
    </div>
));

EraLabelContent.displayName = 'EraLabelContent';

interface MapTimelineControlProps {
    minYear: number;
    maxYear: number;
    currentYear: number;
    eras: TimelineEra[];
    onYearChange: (year: number) => void;
    passageDate?: DateProposal;
    alternativeDates?: DateProposal[];
}

export function MapTimelineControl({
    minYear,
    maxYear,
    currentYear,
    eras,
    onYearChange,
    passageDate,
    alternativeDates
}: MapTimelineControlProps) {
    const isDarkMode = useDarkMode();
    const [isOpen, setIsOpen] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showErasLegend, setShowErasLegend] = useState(false);
    const [hoveredEraIndex, setHoveredEraIndex] = useState<number | null>(null);
    const [hasInteracted, setHasInteracted] = useState(false);
    const playSpeed = 50;

    // Local handler to track interaction
    const handleYearChange = useCallback((year: number) => {
        setHasInteracted(true);
        onYearChange(year);
    }, [onYearChange]);

    // Auto-play functionality
    useEffect(() => {
        if (!isPlaying) return;

        const interval = setInterval(() => {
            const next = currentYear + Math.ceil(playSpeed / 10);
            if (next >= maxYear) {
                setIsPlaying(false);
                handleYearChange(maxYear);
            }
            else {
                handleYearChange(next);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [isPlaying, playSpeed, maxYear, currentYear, handleYearChange]);

    const handlePlayPause = useCallback(() => {
        setHasInteracted(true);
        setIsPlaying(prev => !prev);
    }, []);

    const handleStepBack = useCallback(() => {
        const step = Math.ceil((maxYear - minYear) / 100);
        handleYearChange(Math.max(minYear, currentYear - step));
    }, [currentYear, minYear, maxYear, handleYearChange]);

    const handleStepForward = useCallback(() => {
        const step = Math.ceil((maxYear - minYear) / 100);
        handleYearChange(Math.min(maxYear, currentYear + step));
    }, [currentYear, minYear, maxYear, handleYearChange]);

    const formatYear = useCallback((year: number): string => {
        if (year < 0) {
            return `${Math.abs(year)} BC`;
        }
        return `${year} AD`;
    }, []);

    const currentEra = useMemo(() => {
        return eras.find(era => currentYear >= era.startYear && currentYear <= era.endYear);
    }, [eras, currentYear]);

    const yearToPercent = useCallback((year: number): number => {
        return ((year - minYear) / (maxYear - minYear)) * 100;
    }, [minYear, maxYear]);

    const eraBands = useMemo(() => {
        return eras.map((era, index) => {
            const left = yearToPercent(Math.max(era.startYear, minYear));
            const right = yearToPercent(Math.min(era.endYear, maxYear));
            const width = right - left;
            return { era, index, left, width };
        });
    }, [eras, minYear, maxYear, yearToPercent]);

    const formattedCurrentYear = useMemo(() => formatYear(currentYear), [currentYear, formatYear]);
    const currentYearPercent = useMemo(() => yearToPercent(currentYear), [currentYear, yearToPercent]);

    return (
        <>
            {/* Year overlay - visible when panel is open and current year differs from passage date */}
            <AnimatePresence>
                {isOpen && hasInteracted && (!passageDate || currentYear !== passageDate.startYear) && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute bottom-20 right-4 z-[500]"
                    >
                        <div className={cn(
                            "backdrop-blur-md border rounded-lg px-3 py-2 shadow-lg",
                            isDarkMode
                                ? "bg-black/80 border-white/30"
                                : "bg-white/95 border-black/15"
                        )}>
                            <div className="flex items-center gap-2">
                                <span className={cn(
                                    "text-[10px] uppercase tracking-widest font-bold",
                                    isDarkMode ? "text-white/60" : "text-slate-500"
                                )}>Year</span>
                                <span className={cn(
                                    "text-sm font-bold min-w-[60px] text-center",
                                    isDarkMode ? "text-white" : "text-slate-900"
                                )}>
                                    {formattedCurrentYear}
                                </span>
                            </div>
                            {currentEra && (
                                <div className="flex items-center gap-1.5 mt-1">
                                    <div
                                        className="w-2 h-2 rounded-full"
                                        style={{ backgroundColor: currentEra.color }}
                                    />
                                    <span className={cn(
                                        "text-[10px]",
                                        isDarkMode ? "text-white/70" : "text-slate-600"
                                    )}>{currentEra.label}</span>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Floating button and panel */}
            <div className="absolute bottom-4 right-4 z-[500]">
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ opacity: 0, y: 20, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 20, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className={cn(
                                "absolute bottom-12 right-0 w-[320px] md:w-[380px] backdrop-blur-md border rounded-lg shadow-xl overflow-hidden",
                                isDarkMode
                                    ? "bg-black/85 border-white/30"
                                    : "bg-white/95 border-black/15"
                            )}
                        >
                            {/* Header */}
                            <div className={cn(
                                "flex items-center justify-between px-3 py-2 border-b",
                                isDarkMode ? "border-white/20" : "border-black/10"
                            )}>
                                <h3 className={cn(
                                    "text-sm font-semibold flex items-center gap-2",
                                    isDarkMode ? "text-white" : "text-slate-900"
                                )}>
                                    <Clock className="h-4 w-4" />
                                    Timeline
                                </h3>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setIsOpen(false)}
                                    className={cn(
                                        "h-7 w-7",
                                        isDarkMode
                                            ? "text-white/70 hover:text-white hover:bg-white/10"
                                            : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                                    )}
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>

                            <div className="p-3 space-y-3">
                                {/* Era Chart */}
                                <div
                                    className="relative cursor-pointer"
                                    onClick={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const x = e.clientX - rect.left;
                                        const percent = (x / rect.width) * 100;
                                        const year = Math.round(minYear + (percent / 100) * (maxYear - minYear));
                                        handleYearChange(Math.max(minYear, Math.min(maxYear, year)));
                                    }}
                                >
                                    <div className="relative h-6 bg-white/10 rounded-lg overflow-hidden">
                                        {eraBands.map(({ era, index, left, width }) => {
                                            const isHovered = hoveredEraIndex === index;
                                            return (
                                                <div
                                                    key={index}
                                                    className="absolute h-full transition-all"
                                                    style={{
                                                        left: `${left}%`,
                                                        width: `${width}%`,
                                                        backgroundColor: era.color,
                                                        opacity: isHovered ? 0.9 : 0.6
                                                    }}
                                                    onMouseEnter={() => setHoveredEraIndex(index)}
                                                    onMouseLeave={() => setHoveredEraIndex(null)}
                                                />
                                            );
                                        })}

                                        {/* Current position indicator */}
                                        <div
                                            className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg pointer-events-none z-30"
                                            style={{ left: `${currentYearPercent}%` }}
                                        >
                                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-black/50 rounded-full shadow-md" />
                                        </div>
                                    </div>
                                </div>

                                {/* Play controls */}
                                <div className="flex items-center justify-center gap-2">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={handleStepBack}
                                        disabled={currentYear <= minYear}
                                        className={cn(
                                            "h-8 w-8 disabled:opacity-30",
                                            isDarkMode ? "text-white hover:bg-white/10" : "text-slate-700 hover:bg-slate-100"
                                        )}
                                    >
                                        <SkipBack className="h-3 w-3" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={handlePlayPause}
                                        className={cn(
                                            "h-9 w-9 border",
                                            isDarkMode
                                                ? "text-white hover:bg-white/10 border-white/30"
                                                : "text-slate-700 hover:bg-slate-100 border-black/10"
                                        )}
                                    >
                                        {isPlaying ? (
                                            <Pause className="h-4 w-4" />
                                        ) : (
                                            <Play className="h-4 w-4" />
                                        )}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={handleStepForward}
                                        disabled={currentYear >= maxYear}
                                        className={cn(
                                            "h-8 w-8 disabled:opacity-30",
                                            isDarkMode ? "text-white hover:bg-white/10" : "text-slate-700 hover:bg-slate-100"
                                        )}
                                    >
                                        <SkipForward className="h-3 w-3" />
                                    </Button>
                                </div>

                                {/* Eras toggle */}
                                <button
                                    onClick={() => setShowErasLegend(!showErasLegend)}
                                    className={cn(
                                        "w-full flex items-center justify-between px-2 py-1.5 text-xs rounded transition-colors",
                                        isDarkMode
                                            ? "text-white/70 hover:text-white hover:bg-white/10"
                                            : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                                    )}
                                >
                                    <span className="font-medium">Historical Eras</span>
                                    {showErasLegend ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                </button>

                                {/* Era labels */}
                                <AnimatePresence>
                                    {showErasLegend && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="flex flex-wrap gap-1.5 pt-1">
                                                {eras.map((era, index) => {
                                                    const isHovered = hoveredEraIndex === index;
                                                    return (
                                                        <button
                                                            key={index}
                                                            onClick={() => handleYearChange(era.startYear)}
                                                            onMouseEnter={() => setHoveredEraIndex(index)}
                                                            onMouseLeave={() => setHoveredEraIndex(null)}
                                                            className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-all ${isHovered
                                                                ? 'bg-white/20'
                                                                : 'hover:bg-white/10'
                                                                }`}
                                                            style={isHovered ? {
                                                                boxShadow: `0 0 0 1px ${era.color}`,
                                                            } : {}}
                                                        >
                                                            <div
                                                                className="w-2 h-2 rounded-full flex-shrink-0"
                                                                style={{ backgroundColor: era.color }}
                                                            />
                                                            <span className={isDarkMode ? "text-white/80" : "text-slate-700 font-medium"}>{era.label}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Toggle button and Date Display combined */}
                <div className={cn(
                    "flex items-center backdrop-blur-md border shadow-lg rounded-full overflow-hidden transition-all",
                    isOpen
                        ? isDarkMode
                            ? "bg-black/85 border-primary shadow-primary/20"
                            : "bg-white/95 border-primary shadow-primary/20"
                        : isDarkMode
                            ? "bg-black/75 border-white/20"
                            : "bg-white/90 border-black/10"
                )}>
                    {/* Passage Date part - Compact variant (now on left) */}
                    <PassageDateDisplay
                        variant="compact"
                        passageDate={passageDate}
                        alternativeDates={alternativeDates}
                        overrideDisplayDate={hasInteracted ? formattedCurrentYear : undefined}
                        className={cn(
                            isOpen
                                ? "text-primary"
                                : isDarkMode
                                    ? "text-white"
                                    : "text-slate-900"
                        )}
                    />

                    {/* Vertical separator */}
                    <div className={cn(
                        "w-[1px] h-4",
                        isOpen
                            ? isDarkMode
                                ? "bg-primary-foreground/30"
                                : "bg-primary/30"
                            : isDarkMode
                                ? "bg-white/20"
                                : "bg-black/10"
                    )} />

                    {/* Timeline toggle part (now on right) */}
                    <Button
                        variant="ghost"
                        size="default"
                        onClick={() => setIsOpen(!isOpen)}
                        className={cn(
                            "h-10 min-w-[40px] px-2.5 flex items-center gap-2 rounded-none transition-colors",
                            isOpen
                                ? "text-primary hover:bg-white/10"
                                : isDarkMode
                                    ? "text-white hover:bg-white/10"
                                    : "text-slate-900 hover:bg-slate-200/50"
                        )}
                        title="Timeline"
                    >
                        <Clock className={cn(
                            "h-4 w-4",
                            isOpen ? "text-primary" : (isDarkMode ? "text-white/70" : "text-slate-900")
                        )} />
                    </Button>
                </div>
            </div>
        </>
    );
}
